// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Where a voice is, and how loud it is from there (M8-08).
//
// 06 §4: "spatialised at the speaker's head … **range-limited**: you hear the bay next to you
// clearly and the far end of the room faintly, which is both realistic and a natural moderation
// limit." Both halves of that sentence are requirements, and the second one is the interesting
// one — the falloff is not only an effect, it is the moderation model. A player who wants to be
// unpleasant at somebody has to walk to them, and walking here means teleporting to a bay in
// their line of sight.
//
// **The voice goes where the avatar's head is drawn, and that is one source of truth rather than
// two.** `avatars.ts` already composes a bay with a playfield-local head pose to get a world
// position; this module is handed the same composed point. If the two were computed separately
// they would drift — a remote player's mouth in one place and their voice in another is the
// uncanny half-second that makes a room feel wrong without anybody being able to say why.
//
// **A player with no pose still has a voice.** A windowed browser player never sends a head pose
// at all (06 §4 draws them as an orb), and a headset player's poses go stale whenever their
// network hiccups. Falling silent in either case would be far worse than the imprecision: the bay
// is where they are, to within a step, and it is always known. So the bay is the fallback and the
// head is the refinement.
//
// WHY THE CURVE IS WHAT IT IS
// ---------------------------
// Real inverse-square falloff is wrong for a room of six people standing in a line 1.15 m apart.
// At the reference distance it is fine; two bays away it is already 12 dB down, which is a
// murmur, and three bays away it is inaudible under a pinball machine. That is not "the far end
// of the room faintly" — that is a room where you can only talk to your immediate neighbour.
//
// So the curve is **linear in distance between a near plateau and a far floor**, which gives a
// long usable range and an honest sense of distance without a cliff. It is the same shape a
// `PannerNode` with `distanceModel: 'linear'` produces, and it is stated here rather than left to
// the node so that the mute logic, the tests and the native client all agree about it.

/**
 * Inside this distance a voice is at full level, metres.
 *
 * 1.6 — a little over one bay's spacing (`BAY_SPACING` is 1.15), so the player at the next
 * machine is clear rather than merely audible. Two people at adjacent bays are having a
 * conversation, which is what an arcade sounds like.
 */
export const VOICE_NEAR_M = 1.6

/**
 * Past this distance a voice is at its floor, metres.
 *
 * 7.0 — comfortably the length of a six-bay room, so the far end is quiet but present rather than
 * cut off. A hard cutoff inside the room would be worse than a floor: a voice that vanishes
 * mid-sentence as somebody teleports reads as a bug, where a voice that fades reads as distance.
 */
export const VOICE_FAR_M = 7.0

/**
 * How loud the far end of the room is, as a gain.
 *
 * 0.12 — about 18 dB down, which is audible in a quiet moment and gone under a machine in play.
 * **Not zero**, deliberately: silence at range would make the room feel empty when it is not, and
 * "faintly" is what 06 §4 asks for. It is also what keeps the moderation property honest — being
 * far away makes somebody quiet, not inaudible, so nobody is ever secretly still talking.
 */
export const VOICE_FAR_GAIN = 0.12

/** A point in the room. The same frame the avatars are drawn in. */
export interface VoicePoint {
  readonly x: number
  readonly y: number
  readonly z: number
}

/**
 * How loud a voice at `distance` metres should be, as a gain in 0…1.
 *
 * Linear between the near plateau and the far floor — see the note above on why this is not
 * inverse-square. Clamped at both ends, so a listener standing inside somebody else is not louder
 * than full and the far end of a bigger room is not negative.
 */
export function voiceGainAt(distance: number): number {
  if (!Number.isFinite(distance)) return VOICE_FAR_GAIN
  if (distance <= VOICE_NEAR_M) return 1
  if (distance >= VOICE_FAR_M) return VOICE_FAR_GAIN
  const travelled = (distance - VOICE_NEAR_M) / (VOICE_FAR_M - VOICE_NEAR_M)
  return 1 - travelled * (1 - VOICE_FAR_GAIN)
}

/** Straight-line distance between two points in the room. */
export function distanceBetween(a: VoicePoint, b: VoicePoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z)
}

/**
 * How loud a voice is for a listener here, speaking from there.
 *
 * The one call the playback layer makes when it is placing a voice by gain alone — a runtime with
 * no panner, or a listener whose own pose is not yet known.
 */
export function voiceGainBetween(listener: VoicePoint, speaker: VoicePoint): number {
  return voiceGainAt(distanceBetween(listener, speaker))
}

/**
 * Where a speaker's voice comes from.
 *
 * **The head when there is one, the bay when there is not.** Head height is used as given rather
 * than clamped: a player who is sitting down really is speaking from lower than one who is
 * standing, and a headset that has reported an impossible floor is already being corrected
 * upstream by `floorAlignment`.
 *
 * `headY` falls back to a mouth height above the bay rather than to the floor. A voice emitted at
 * ankle level in an HRTF is not "a slightly wrong position" — it is a person under the floorboards,
 * which is the single most noticeable way to get spatial audio wrong.
 */
export const NOMINAL_MOUTH_HEIGHT_M = 1.55

export function voiceOrigin(
  bay: { readonly x: number; readonly z: number },
  head: VoicePoint | undefined,
): VoicePoint {
  if (head === undefined) return { x: bay.x, y: NOMINAL_MOUTH_HEIGHT_M, z: bay.z }
  return head
}
